import { DEFAULT_ISO_LANGUAGE_REGION } from '@brandfolder/utilities';
import classnames from 'classnames';
import React, { useRef, useState, useEffect, useContext } from 'react';
import { useLayer } from 'react-laag';
import { Transition } from 'react-transition-group';
import styled, { ThemeContext, ThemeProvider } from 'styled-components';
import { ButtonLooks } from '../../../../enums/button.js';
import { BrandfolderHexColors } from '../../../../enums/colors.js';
import { ComboboxPlacements } from '../../../../enums/combobox.js';
import { FontIcons } from '../../../../enums/font-icon.js';
import { InputLabelPositions, InputLooks } from '../../../../enums/input.js';
import { Keysets } from '../../../../enums/keys.js';
import { Transitions } from '../../../../enums/transitions.js';
import { useClickOutside } from '../../../../hooks/use-click-outside.js';
import { useDebounce } from '../../../../hooks/use-debounce.js';
import { generateKey } from '../../../../utilities/key.js';
import '../../../../utilities/color.js';
import { getContainer } from '../../../../utilities/container.js';
import 'date-fns';
import '../../../../utilities/locale.js';
import { mergeRefs } from '../../../../utilities/refs.js';
import { useHover } from '../../../../hooks/use-hover.js';
import { usePrefersReducedMotion } from '../../../../hooks/use-prefers-reduced-motion.js';
import { preventKeyboardScrollingKeyboardEvent } from '../../../../hooks/use-prevent-keyboard-scrolling.js';
import '../../../../styles/styled-components/alert.js';
import '../../../../styles/styled-components/animations.js';
import '../../../../styles/styled-components/box-shadow.js';
import '../../../../styles/styled-components/button.js';
import '../../../../themes/alert.js';
import { borderRadiusLarge } from '../../../../themes/borders.js';
import '../../../../themes/button.js';
import '../../../../themes/checkbox.js';
import '../../../../themes/divider.js';
import '../../../../themes/dropdown.js';
import '../../../../themes/fieldset.js';
import '../../../../themes/input.js';
import '../../../../themes/list.js';
import '../../../../themes/listbox.js';
import '../../../../themes/loader.js';
import '../../../../themes/option.js';
import '../../../../themes/popout.js';
import '../../../../themes/switch.js';
import { lightTheme } from '../../../../themes/themes.js';
import '../../../../themes/tooltip.js';
import '../../../../styles/styled-components/font-face.js';
import '../../../../styles/styled-components/global.js';
import { baseInputStyle, getInputBorderRadius, getInputPadding, getInputTheme, getInputHoveredBorderColor, getInputFocusedBorderColor, getInputPlaceholderColor } from '../../../../styles/styled-components/input.js';
import '../../../../styles/styled-components/list.js';
import '../../../../styles/styled-components/listitem.js';
import '../../../../styles/styled-components/media-queries.js';
import '../../../../styles/styled-components/reset.js';
import '../../../../styles/styled-components/outline.js';
import '../../../../styles/styled-components/popout.js';
import '../../../../styles/styled-components/screen-reader.js';
import '../../../../styles/styled-components/switch.js';
import '../../../../styles/styled-components/tooltip.js';
import { transitionFadeIn } from '../../../../styles/transitions/fade-in.js';
import { transitionFadeInSlide } from '../../../../styles/transitions/fade-in-slide.js';
import '../../../atoms/icon/font-icon/font-icon.js';
import '../../../atoms/alert/inline-alert/inline-alert.js';
import '../../../atoms/alert/standard-alert/standard-alert.js';
import 'boring-avatars';
import '../../../atoms/tooltip/hover-tooltip/hover-tooltip.js';
import '../../../atoms/button/close-button/close-button.js';
import '../../../atoms/button/color-button/color-button.js';
import { IconButton } from '../../../atoms/button/icon-button/icon-button.js';
import '../../../atoms/button/standard-button/standard-button.js';
import '../../../atoms/loader/circle-loader/circle-loader.js';
import '../../../atoms/loader/shimmer-loader/shimmer-loader.js';
import '../../../atoms/divider/standard-divider/standard-divider.js';
import '../../../atoms/fieldset/standard-fieldset/standard-fieldset.js';
import '../../../atoms/heading/standard-heading/standard-heading.js';
import '../../../atoms/label/standard-label/standard-label.js';
import '../../../atoms/link/button-link/button-link.js';
import '../../../atoms/link/standard-link/standard-link.js';
import '../../../atoms/popout/standard-popout/standard-popout.js';
import '../../../atoms/screen-reader/only-for-screen-reader/only-for-screen-reader.js';
import '../../../atoms/table/standard-table/caption.js';
import '../../../atoms/table/standard-table/tbody.js';
import '../../../atoms/table/standard-table/tr-empty.js';
import '../../../atoms/table/standard-table/td.js';
import '../../../atoms/table/standard-table/tr.js';
import '../../../atoms/table/standard-table/table.js';
import '../../../atoms/table/standard-table/tfoot.js';
import '../../../atoms/table/standard-table/th.js';
import '../../../atoms/table/standard-table/thead.js';
import '../../../atoms/table/dnd-table/dnd-handle-td.js';
import '../../../atoms/table/dnd-table/dnd-table-drag-context.js';
import '../../../atoms/text/help-text/help-text.js';
import '../../../atoms/text/standard-text/standard-text.js';
import '../../../internal/list/standard-list/standard-list.js';
import { StandardListbox } from '../../../internal/listbox/standard-listbox/standard-listbox.js';
import '../../../internal/listitem/standard-listitem/standard-listitem.js';
import '../../../internal/option/standard-option/standard-option.js';
import '../../../internal/textarea/fake-textarea.js';
import '../../../internal/textfield/textfield-addon.js';
import { TextfieldBody } from '../../../internal/textfield/textfield-body.js';
import { TextfieldHeader } from '../../../internal/textfield/textfield-header.js';
import { TextfieldIcon, TextfieldIconTypes } from '../../../internal/textfield/textfield-icon.js';
import { TextfieldInner } from '../../../internal/textfield/textfield-inner.js';
import { TextfieldWrapper } from '../../../internal/textfield/textfield-wrapper.js';
import { BaseCombobox } from '../base-combobox/base-combobox.js';

const DEFAULT_SELECT_FILTER = (options)=>!!options.option.search?.toLocaleLowerCase(options.locale).startsWith(options.value.toLocaleLowerCase(options.locale));
const DEFAULT_EDITABLE_FILTER = (options)=>!!options.option.search?.toLocaleLowerCase(options.locale).includes(options.value.toLocaleLowerCase(options.locale));
const BaseStandardCombobox = (props)=>{
    const { after, afterProps, allowCustomValue, allowOptionsWrapping, before, beforeProps, bodyProps, className, clearAfterCustomValue, closeOnClickAway, comboboxDivProps, comboboxTextareaProps, disabled, disabledOptions, duplicate, editable, ellipsis, error, errorProps, expandOnHover, headerProps, helpText, helpTextId, helpTextProps, iconButtonProps, id, initialExpanded, initialSelectedIndex, inlineAlertId, innerProps, insidePopout, labelId, labelPosition, labelProps, labels, leftIcon, leftIconProps, listboxProps, loading, locale = DEFAULT_ISO_LANGUAGE_REGION, look, onExpanded, onSelection, optionProps, options, parentScrolls, placement, possiblePlacements = [
        ComboboxPlacements.BottomStart,
        ComboboxPlacements.TopStart
    ], readOnly, required, reset, search, searchDebounce, searchFilter, searchMinLength, showEmptyOption, showIconButton, showLabel, showLoadingOption, showPlaceholderOption, tagger, toggleExpanded, transition, transitionDelay, transitionDuration, virtualized, ...otherProps } = props;
    const { emptyLabel, iconButtonLabel, label, listboxLabel, loaderLabel, requiredLabel } = labels;
    const comboboxBodyId = `${id}-combobox-body`;
    const helpId = helpTextId || `${id}-help-text`;
    const alertId = inlineAlertId || `${id}-inline-alert`;
    const labId = labelId || `${id}-label`;
    const listboxId = `${id}-listbox`;
    const comboboxId = `${id}-combobox`;
    const wrapperId = `${id}-wrapper`;
    const hasInitialSelectedIndex = initialSelectedIndex !== undefined && initialSelectedIndex > -1;
    const initialIndex = hasInitialSelectedIndex ? initialSelectedIndex : editable ? undefined : 0;
    const hasInitialIndex = initialIndex !== undefined;
    const hasAfter = !!after;
    const hasBefore = !!before;
    const hasCustomEditable = allowCustomValue && editable;
    const hasError = !!error;
    const hasHelpText = !!helpText;
    const hasLeftIcon = !!leftIcon;
    const hasOptions = options.length > 0;
    const hasRequired = required;
    const isLoading = loading === true && !!loaderLabel;
    const hasLoadingOption = editable && showLoadingOption;
    const loadingDisabled = !editable && isLoading || editable && !hasLoadingOption && isLoading;
    const searchable = editable && search;
    const notSearchable = editable && !search;
    const prefersReducedMotion = usePrefersReducedMotion();
    const wrapperRef = useRef(null);
    const { clickedOutside } = useClickOutside({
        ref: wrapperRef
    });
    const divRef = useRef(null);
    const nodeRef = useRef(null);
    const textareaRef = useRef(null);
    const getDisabledOptions = (opts)=>{
        if (!disabledOptions || opts.length === 0) {
            return opts;
        }
        return opts.map((option)=>{
            const opt = disabledOptions.find((disabledOption)=>option.key === disabledOption.key || option.key === disabledOption.value);
            option.disabled = !!opt;
            return option;
        });
    };
    const getListboxOptions = (opts)=>{
        const disabledOptions = getDisabledOptions(opts);
        const firstOption = disabledOptions[0];
        if (!showPlaceholderOption && !!firstOption?.placeholder) return disabledOptions.splice(1);
        return [
            ...disabledOptions
        ];
    };
    const [expanded, setExpanded] = useState(initialExpanded || false);
    const [focused, setFocused] = useState(false);
    const [selected, setSelected] = useState(false);
    const [touched, setTouched] = useState(false);
    const [listboxOptions, setListboxOptions] = useState(getListboxOptions(options));
    const hasListboxOptions = listboxOptions.length > 0;
    const hasEnabledListboxOptions = listboxOptions.some((option)=>!option.disabled);
    const hasFetchedOptions = hasListboxOptions && !notSearchable;
    const [selectedIndex, setSelectedIndex] = useState(initialIndex);
    const hasSelectedIndex = selectedIndex !== undefined;
    const hasInitialOption = hasListboxOptions && hasInitialIndex;
    const initialOption = hasInitialOption ? listboxOptions[initialIndex] : null;
    const [selectedOption, setSelectedOption] = useState(initialOption);
    const initialValue = editable && initialOption ? initialOption.value.toString().trim() : '';
    const [value, setValue] = useState('');
    const valueDebounced = useDebounce(value, searchDebounce);
    const canShowEmpty = showEmptyOption && !!emptyLabel;
    const hasEmptyOption = searchable ? canShowEmpty && !isLoading && touched && !hasListboxOptions && valueDebounced : canShowEmpty && !isLoading && touched && !hasOptions && valueDebounced;
    useEffect(()=>{
        if (editable && initialValue && !selected && !touched) {
            setValue(initialValue);
        }
    }, [
        initialValue
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(()=>{
        if (notSearchable && !selected && touched && !value) {
            toggleExpand(true);
        }
    }, [
        value
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(()=>{
        if (!editable) {
            /**
       * This reproduces when focused on a `<select>` and typing.
       *
       * Opens the listbox and moves visual focus
       * to the first option that matches the typed character.
       *
       * If multiple keys are typed in quick succession,
       * visual focus moves to the first option that matches the full string.
       */ if (valueDebounced) {
                const optionIndex = options.findIndex((option)=>searchFilter ? searchFilter({
                        option,
                        value: valueDebounced,
                        locale
                    }) : DEFAULT_SELECT_FILTER({
                        option,
                        value: valueDebounced,
                        locale
                    }));
                if (optionIndex !== -1) {
                    const option = listboxOptions[optionIndex];
                    toggleExpand(true);
                    if (!option.disabled) {
                        setSelectedIndex(optionIndex);
                    }
                }
                setValue('');
            }
        } else if (searchable && touched) {
            /**
       * Moves visual focus to the textbox.
       *
       * Types the character in the textbox.
       *
       * Options in the listbox are filtered based on characters in the textbox.
       */ if (valueDebounced && valueDebounced.length > searchMinLength && !selected) {
                const searchedOptions = options.filter((option)=>searchFilter ? searchFilter({
                        option,
                        value: valueDebounced,
                        locale
                    }) : DEFAULT_EDITABLE_FILTER({
                        option,
                        value: valueDebounced,
                        locale
                    }));
                setListboxOptions(searchedOptions);
            } else {
                setListboxOptions(options);
            }
            if (searchMinLength === 0 || valueDebounced.length > searchMinLength) {
                toggleExpand(true);
            }
            setSelected(false);
            setSelectedIndex(undefined);
        } else if (!notSearchable && !selected && touched && valueDebounced) {
            toggleExpand(true);
            setSelected(false);
            setSelectedIndex(undefined);
        }
    }, [
        valueDebounced
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    /**
   * IMPORTANT: `react-laag` also has a `useHover` hook, but it doesn't expose
   * a `setHovered` to manually turn hovering off when we select an option.
   */ const { hoverProps, hovered, setHovered } = useHover();
    useEffect(()=>{
        if (expandOnHover) {
            setExpanded(hovered);
        }
    }, [
        expandOnHover,
        hovered
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    const canExpand = !disabled && !loadingDisabled && !readOnly;
    const isExpanded = canExpand && expanded;
    const toggleExpand = (toggle)=>{
        if (canExpand && toggle !== undefined) {
            setExpanded(toggle);
            if (toggle === false) {
                setHovered(false);
            }
        } else if (canExpand) {
            setExpanded((previousExpanded)=>!previousExpanded);
            if (!expanded === false) {
                setHovered(false);
            }
        }
    };
    const resetSelected = ()=>{
        setSelectedOption(initialOption);
        setSelectedIndex(initialIndex);
    };
    useEffect(()=>{
        if (reset) {
            setListboxOptions(getListboxOptions(options));
            toggleExpand(false);
            setTouched(false);
            setValue('');
            resetSelected();
        }
    }, [
        reset
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(()=>{
        if (hasLoadingOption && isLoading && touched) {
            toggleExpand(true);
        }
    }, [
        hasLoadingOption,
        isLoading,
        touched
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(()=>{
        if (canExpand && toggleExpanded !== undefined) {
            toggleExpand(toggleExpanded);
        }
    }, [
        toggleExpanded
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(()=>{
        if (focused) {
            // prevents window jumping when home and enter are pressed and ONLY when combobox is focused
            const listener = (e)=>preventKeyboardScrollingKeyboardEvent(e, Keysets.ComboboxInput);
            window.addEventListener('keydown', listener, false);
            return ()=>window.removeEventListener('keydown', listener);
        }
        return;
    }, [
        focused
    ]);
    useEffect(()=>{
        if (onExpanded) {
            onExpanded(isExpanded);
        }
    }, [
        isExpanded
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(()=>{
        if (disabledOptions && !reset) {
            setListboxOptions(getListboxOptions(listboxOptions));
        }
    }, [
        disabledOptions
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(()=>{
        setListboxOptions(getListboxOptions(options));
    }, [
        options
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(()=>{
        if (insidePopout && clickedOutside && closeOnClickAway) {
            toggleExpand(false);
        }
    }, [
        clickedOutside
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    const resetCombobox = ()=>{
        toggleExpand(false);
        setHovered(false);
        setTouched(false);
        focusCombobox();
        setTimeout(()=>{
            setSelected(false);
        }, 50);
    };
    /**
   * Sets the value to the content of the focused option in the listbox.
   * Closes the listbox.
   * Sets visual focus on the combobox.
   */ const onSelected = (index)=>{
        setSelected(true);
        const option = index === undefined ? null : listboxOptions[index];
        if (notSearchable || hasCustomEditable) {
            resetSelected();
        } else {
            setSelectedOption(option);
            setSelectedIndex(index);
        }
        if (option) {
            onSelection(option, index, option.value);
        }
        if (clearAfterCustomValue && editable || !editable) {
            setValue('');
        } else if (option) {
            setValue(option.value.toString().trim());
        }
        resetCombobox();
    };
    const onManualSelection = ()=>{
        if (!duplicate) {
            setSelected(true);
            resetSelected();
            onSelection(null, undefined, value);
            if (clearAfterCustomValue) {
                setValue('');
            }
            resetCombobox();
        }
    };
    const focusCombobox = ()=>{
        if (!editable && divRef.current) {
            divRef.current.focus({
                preventScroll: true
            });
        } else if (textareaRef.current) {
            textareaRef.current.focus({
                preventScroll: true
            });
        }
    };
    const getFirstEnabledOptionIndex = ()=>{
        return listboxOptions.findIndex((option)=>!option.disabled);
    };
    const getLastEnabledOptionIndex = ()=>{
        // iterate backwards through the options and break when we find one that's not disabled
        // (neither lastIndexOf or reverse().findIndex give us correct index)
        for(let i = listboxOptions.length - 1; i >= 0; i--){
            if (!listboxOptions[i].disabled) return i;
        }
        return undefined;
    };
    const getFirstOption = ()=>{
        if (hasListboxOptions && hasEnabledListboxOptions) {
            return getFirstEnabledOptionIndex();
        }
        return;
    };
    const getLastOption = ()=>{
        if (hasListboxOptions && hasEnabledListboxOptions) {
            return getLastEnabledOptionIndex();
        }
        return;
    };
    const getNextOption = (index)=>{
        // if no listboxOptions or all listboxOptions are disabled, do nothing
        if (!hasListboxOptions || !hasEnabledListboxOptions) return;
        const nextIndex = index + 1;
        const lastEnabledIndex = getLastEnabledOptionIndex();
        const atLast = index === lastEnabledIndex;
        const nextOption = listboxOptions[atLast ? lastEnabledIndex : nextIndex];
        // if we've selected the last enabled option and can wrap
        // go to the first enabled option
        if (atLast && allowOptionsWrapping) {
            return getFirstEnabledOptionIndex();
        }
        // if the next option isn't disabled and we're not at the last enabled option
        // select that next option
        if (!nextOption.disabled && !atLast) {
            return nextIndex;
        }
        // otherwise we need to recursively keep trying to find
        // another next option that isn't disabled
        return getNextOption(nextIndex);
    };
    const getPreviousOption = (index)=>{
        // if no listboxOptions or all listboxOptions are disabled, do nothing
        if (!hasListboxOptions || !hasEnabledListboxOptions) return;
        const previousIndex = index - 1;
        const firstEnabledIndex = getFirstEnabledOptionIndex();
        const atFirst = index === firstEnabledIndex;
        const previousOption = listboxOptions[atFirst ? firstEnabledIndex : previousIndex];
        // if we've selected the first enabled option and can wrap
        // go to the last enabled option
        if (atFirst && allowOptionsWrapping) {
            return getLastEnabledOptionIndex();
        }
        // if the previous option isn't disabled and we're not at the first enabled option
        // select that previous option
        if (!previousOption.disabled && !atFirst) {
            return previousIndex;
        }
        // otherwise we need to recursively keep trying to find
        // another previous option that isn't disabled
        return getPreviousOption(previousIndex);
    };
    const moveInputCursor = (move)=>{
        if (textareaRef.current) {
            const offset = move === 'end' ? value.length : 0;
            textareaRef.current.setSelectionRange(offset, offset);
        }
    };
    const onOutsideClick = ()=>{
        if (closeOnClickAway) toggleExpand(false);
    };
    const { layerProps, layerSide, renderLayer, triggerProps } = useLayer({
        auto: true,
        container: getContainer(comboboxBodyId),
        containerOffset: 5,
        isOpen: isExpanded,
        placement,
        possiblePlacements,
        onOutsideClick,
        overflowContainer: true,
        snap: true,
        triggerOffset: 10
    });
    /**
   * IMPORTANT: The `react-laag` `style` prop is only used if `parentScrolls` prop is `true`.
   */ const { ref, style, ...otherLayerProps } = layerProps;
    /**
   * Setting delay and duration to zero will close the listbox immediately without animating out
   */ const overrideDuration = selected && tagger;
    const delay = overrideDuration ? 0 : transitionDelay;
    const duration = overrideDuration ? 0 : transitionDuration;
    const timeout = delay > 0 ? delay + duration : duration;
    return /*#__PURE__*/ React.createElement(TextfieldWrapper, {
        ...otherProps,
        className: classnames({
            'bf-combobox__standard': true,
            'bf-combobox__standard--after': hasAfter,
            'bf-combobox__standard--before': hasBefore,
            'bf-combobox__standard--disabled': disabled,
            'bf-combobox__standard--error': hasError,
            'bf-combobox__standard--expanded': isExpanded,
            'bf-combobox__standard--editable': editable,
            'bf-combobox__standard--empty': hasEmptyOption,
            'bf-combobox__standard__focused': focused,
            'bf-combobox__standard--help-text': hasHelpText,
            'bf-combobox__standard--hovered': hovered,
            'bf-combobox__standard--label-hidden': !showLabel,
            [`bf-combobox__standard--label-${labelPosition}`]: showLabel,
            'bf-combobox__standard--loading': isLoading,
            [`bf-combobox__standard--locale-${locale}`]: locale,
            [`bf-combobox__standard--look-${look}`]: look,
            [`bf-combobox__standard--position-${layerSide}`]: layerSide,
            'bf-combobox__standard--readonly': readOnly,
            'bf-combobox__standard--required': hasRequired,
            [`bf-combobox__standard--transition-${transition}`]: transition,
            [`${className}`]: !!className
        }),
        id: wrapperId,
        labelPosition: labelPosition,
        ref: wrapperRef
    }, /*#__PURE__*/ React.createElement(TextfieldHeader, {
        ...headerProps,
        className: classnames({
            'bf-combobox__header': true,
            [`${headerProps?.className}`]: !!headerProps?.className
        }),
        error: error,
        errorProps: errorProps,
        hidden: tagger,
        htmlFor: comboboxId,
        inlineAlertId: alertId,
        label: label,
        labelId: labId,
        labelPosition: labelPosition,
        labelProps: {
            ...labelProps,
            onClick: ()=>{
                focusCombobox();
                toggleExpand(true);
            }
        },
        required: required,
        requiredLabel: requiredLabel,
        showLabel: showLabel
    }), /*#__PURE__*/ React.createElement(TextfieldBody, {
        ...bodyProps,
        ...triggerProps,
        ...hoverProps,
        after: after,
        afterProps: afterProps,
        before: before,
        beforeProps: beforeProps,
        className: classnames({
            'bf-combobox__body': true,
            [`${bodyProps?.className}`]: !!bodyProps?.className
        }),
        "data-testid": comboboxBodyId,
        disabled: disabled,
        error: error,
        errorProps: errorProps,
        helpText: helpText,
        helpTextId: helpId,
        helpTextProps: helpTextProps,
        hidden: tagger,
        id: comboboxBodyId,
        inlineAlertId: alertId,
        labelPosition: labelPosition,
        look: look
    }, /*#__PURE__*/ React.createElement(TextfieldInner, {
        ...innerProps,
        className: classnames({
            'bf-combobox__inner': true,
            [`${innerProps?.className}`]: !!innerProps?.className
        })
    }, hasLeftIcon && /*#__PURE__*/ React.createElement(TextfieldIcon, {
        ...leftIconProps,
        className: classnames({
            'bf-combobox__icon': true,
            'bf-combobox__left-icon': true,
            [`${leftIconProps?.className}`]: !!leftIconProps?.className
        }),
        disabled: disabled,
        error: error,
        icon: leftIcon,
        look: look,
        type: TextfieldIconTypes.Left
    }), /*#__PURE__*/ React.createElement(BaseCombobox, {
        "aria-activedescendant": isExpanded && hasSelectedIndex ? listboxOptions[selectedIndex]?.key.toString() : undefined,
        "aria-controls": listboxId,
        "aria-describedby": hasError ? alertId : hasHelpText ? helpId : undefined,
        "aria-expanded": isExpanded,
        "aria-invalid": hasError,
        "aria-labelledby": error ? alertId : labId,
        className: classnames({
            'bf-combobox__combobox': true,
            'bf-combobox__combobox--focused': focused,
            'bf-combobox__combobox--hovered': hovered,
            'bf-combobox__combobox--placeholder': !editable && !!selectedOption?.placeholder
        }),
        disabled: disabled || loadingDisabled,
        divProps: {
            ...comboboxDivProps,
            children: selectedOption?.placeholder || selectedOption?.children || null,
            onBlur: (e)=>{
                setFocused(false);
                if (comboboxDivProps?.onBlur) {
                    comboboxDivProps.onBlur(e);
                }
            },
            onClick: (e)=>{
                focusCombobox();
                toggleExpand();
                if (comboboxDivProps?.onClick) {
                    comboboxDivProps.onClick(e);
                }
            },
            onFocus: (e)=>{
                setFocused(true);
                if (comboboxDivProps?.onFocus) {
                    comboboxDivProps.onFocus(e);
                }
            },
            onKeyDown: (e)=>{
                const { key } = e;
                if (key === 'ArrowDown') {
                    if (!isExpanded) {
                        /**
                     * Opens the listbox if it is not already displayed without moving focus or changing selection.
                     * DOM focus remains on the combobox.
                     */ toggleExpand(true);
                    } else if (hasSelectedIndex) {
                        /**
                     * Moves visual focus to the next option. If visual focus is on the last option, visual focus does not move.
                     */ setSelectedIndex(getNextOption(selectedIndex));
                    }
                }
                if (key === 'ArrowUp') {
                    if (!isExpanded) {
                        /**
                     * First opens the listbox if it is not already displayed and then moves visual focus to the first option.
                     * DOM focus remains on the combobox.
                     */ setSelectedIndex(getFirstOption());
                        toggleExpand(true);
                    } else if (hasSelectedIndex) {
                        /**
                     * Moves visual focus to the previous option.
                     * If visual focus is on the first option, visual focus does not move.
                     */ setSelectedIndex(getPreviousOption(selectedIndex));
                    }
                }
                if (key === 'Tab') {
                    /**
                   * Sets the value to the content of the focused option in the listbox.
                   * Closes the listbox.
                   * Performs the default action, moving focus to the next focusable element.
                   */ onSelected(selectedIndex);
                    toggleExpand(false);
                }
                if (comboboxDivProps?.onKeyDown) {
                    comboboxDivProps.onKeyDown(e);
                }
            },
            onKeyUp: (e)=>{
                const { key } = e;
                if (key === 'End') {
                    if (!isExpanded) {
                        /**
                     * Opens the listbox and moves visual focus to the last option.
                     */ toggleExpand(true);
                    }
                    /**
                   * Moves visual focus to the last option.
                   */ setSelectedIndex(getLastOption());
                }
                if (key === 'Home') {
                    /**
                   * Opens the listbox and moves visual focus to the first option.
                   */ if (!isExpanded) {
                        toggleExpand(true);
                    }
                    setSelectedIndex(getFirstOption());
                }
                if (key === 'Enter' || key === ' ' || key === 'Space') {
                    if (isExpanded) {
                        /**
                     * Sets the value to the content of the focused option in the listbox.
                     * Closes the listbox.
                     * Sets visual focus on the combobox.
                     */ onSelected(selectedIndex);
                    } else {
                        /**
                     * Opens the listbox without moving focus or changing selection.
                     */ toggleExpand(true);
                    }
                }
                if (key === 'Escape') {
                    /**
                   * Closes the listbox. Sets visual focus on the combobox.
                   */ focusCombobox();
                    toggleExpand(false);
                }
                /**
                 * Matches a single unicode character
                 */ if (/^.$/u.test(key)) {
                    setValue(value + key);
                }
                /**
                 * There is no `e.target.value`, so we need to handle this ourselves
                 */ if (key === 'Backspace' && value) {
                    setValue(value.slice(0, -1));
                }
                if (comboboxDivProps?.onKeyUp) {
                    comboboxDivProps.onKeyUp(e);
                }
            },
            ref: mergeRefs([
                comboboxDivProps?.ref,
                divRef
            ])
        },
        editable: editable,
        id: comboboxId,
        readOnly: readOnly,
        required: hasRequired,
        textareaProps: {
            ...comboboxTextareaProps,
            'aria-autocomplete': 'list',
            onBlur: (e)=>{
                setFocused(false);
                if (comboboxTextareaProps?.onBlur) {
                    comboboxTextareaProps.onBlur(e);
                }
            },
            onChange: (e)=>{
                setTouched(true);
                /**
                 * Types the character in the textbox.
                 * Options in the listbox are filtered based on characters in the textbox.
                 */ setValue(e.target.value);
                if (comboboxTextareaProps?.onChange) {
                    comboboxTextareaProps.onChange(e);
                }
            },
            onClick: (e)=>{
                if (hasListboxOptions) {
                    toggleExpand();
                }
                if (comboboxTextareaProps?.onClick) {
                    comboboxTextareaProps.onClick(e);
                }
            },
            onFocus: (e)=>{
                setFocused(true);
                if (comboboxTextareaProps?.onFocus) {
                    comboboxTextareaProps.onFocus(e);
                }
            },
            onKeyDown: (e)=>{
                const { key } = e;
                if (key === 'ArrowDown') {
                    if (!isExpanded && (search || hasFetchedOptions)) {
                        /**
                     * If the textbox is empty and the listbox is not displayed, opens the listbox and moves visual focus to the first option.
                     */ toggleExpand(true);
                        setSelectedIndex(getFirstOption());
                    } else if (isExpanded && !hasSelectedIndex) {
                        /**
                     * If the textbox is not empty and the listbox is displayed, moves visual focus to the first suggested value.
                     */ setSelectedIndex(getFirstOption());
                    } else if (hasSelectedIndex) {
                        /**
                     * Moves visual focus to the next option.
                     *
                     * If visual focus is on the last option, moves visual focus to the first option.
                     */ setSelectedIndex(getNextOption(selectedIndex));
                    }
                }
                if (key === 'ArrowUp') {
                    if (!isExpanded && (search || hasFetchedOptions)) {
                        /**
                     * If the textbox is empty, first opens the listbox if it is not already displayed and then moves visual focus to the last option.
                     */ toggleExpand(true);
                        setSelectedIndex(getLastOption());
                    } else if (isExpanded && !hasSelectedIndex) {
                        /**
                     * If the textbox is not empty and the listbox is displayed, moves visual focus to the last suggested value.
                     */ setSelectedIndex(getLastOption());
                    } else if (hasSelectedIndex) {
                        /**
                     * Moves visual focus to the previous option.
                     *
                     * If visual focus is on the first option, moves visual focus to the last option.
                     */ setSelectedIndex(getPreviousOption(selectedIndex));
                    }
                }
                if (key === 'Enter') {
                    // prevents parent <form> from submitting when Enter key is pressed
                    // (both creating a custom option and selecting a role="option")
                    e.preventDefault();
                }
                if (key === 'Tab') {
                    toggleExpand(false);
                }
                if (comboboxTextareaProps?.onKeyDown) {
                    comboboxTextareaProps.onKeyDown(e);
                }
            },
            onKeyUp: (e)=>{
                const { key } = e;
                if (key === 'End') {
                    /**
                   * Moves visual focus to the textbox and places the editing cursor at the end of the field.
                   */ focusCombobox();
                    moveInputCursor('end');
                }
                if (key === 'Home') {
                    /**
                   * Moves visual focus to the textbox and places the editing cursor at the beginning of the field.
                   */ focusCombobox();
                    moveInputCursor('start');
                }
                if (key === 'Enter') {
                    if (hasCustomEditable && !hasSelectedIndex) {
                        onManualSelection();
                    } else if (!isExpanded && (search || hasFetchedOptions)) {
                        /**
                     * Opens the listbox without moving focus or changing selection.
                     */ toggleExpand(true);
                    } else if (hasSelectedIndex) {
                        /**
                     * Sets the textbox value to the content of the focused option in the listbox.
                     * Closes the listbox.
                     * Sets visual focus on the textbox.
                     */ onSelected(selectedIndex);
                    } else {
                        /**
                     * Closes the listbox if it is displayed.
                     */ toggleExpand(false);
                    }
                }
                if (key === 'Escape') {
                    if (isExpanded) {
                        /**
                     * Closes the listbox. Sets visual focus on the textbox.
                     */ focusCombobox();
                        toggleExpand(false);
                    } else {
                        /**
                     * If the listbox is not displayed, clears the textbox.
                     */ setTouched(false);
                        setValue('');
                    }
                }
                if (comboboxTextareaProps?.onKeyUp) {
                    comboboxTextareaProps.onKeyUp(e);
                }
            },
            placeholder: comboboxTextareaProps?.placeholder || options[0]?.placeholder,
            ref: mergeRefs([
                comboboxTextareaProps?.ref,
                textareaRef
            ]),
            value
        }
    }), /*#__PURE__*/ React.createElement(IconButton, {
        ...iconButtonProps,
        "aria-controls": listboxId,
        "aria-expanded": isExpanded,
        disabled: disabled || isLoading || readOnly,
        icon: isExpanded && !disabled && !isLoading ? FontIcons.CaretUp : FontIcons.CaretDown,
        label: iconButtonLabel,
        loaderLabel: loaderLabel,
        loading: isLoading,
        look: ButtonLooks.Default,
        onClick: ()=>{
            focusCombobox();
            toggleExpand();
        },
        showIconButton: showIconButton,
        tabIndex: -1
    }), renderLayer(/*#__PURE__*/ React.createElement(Transition, {
        appear: isExpanded,
        in: isExpanded,
        mountOnEnter: true,
        nodeRef: nodeRef,
        timeout: timeout,
        unmountOnExit: true
    }, (transitionStatus)=>{
        return /*#__PURE__*/ React.createElement(StandardListbox, {
            ...listboxProps,
            ...otherLayerProps,
            "aria-label": listboxLabel,
            "aria-labelledby": labId,
            ellipsis: ellipsis,
            id: listboxId,
            initialSelectedIndex: initialSelectedIndex,
            keyset: editable ? Keysets.ComboboxInput : Keysets.ComboboxDiv,
            onSelected: onSelected,
            options: hasLoadingOption && isLoading ? [
                {
                    children: loaderLabel,
                    disabled: true,
                    key: generateKey(),
                    value: ''
                }
            ] : hasEmptyOption ? [
                {
                    children: emptyLabel,
                    disabled: true,
                    key: generateKey(),
                    value: ''
                }
            ] : listboxOptions,
            optionProps: optionProps,
            ref: mergeRefs([
                nodeRef,
                ref
            ]),
            selectedIndex: selectedIndex,
            style: {
                ...listboxProps?.style,
                ...!prefersReducedMotion && transition === Transitions.FadeIn && transitionDuration > 0 && transitionFadeIn({
                    transitionDelay: delay,
                    transitionDuration: duration,
                    transitionStatus
                }),
                ...!prefersReducedMotion && transition === Transitions.FadeInSlide && transitionDuration > 0 && transitionFadeInSlide({
                    layerSide,
                    transitionDelay: delay,
                    transitionDuration: duration,
                    transitionStatus
                }),
                ...parentScrolls && style
            },
            virtualized: virtualized
        });
    })))));
};
const Combobox = styled(BaseStandardCombobox)`
  .bf-combobox__combobox {
    ${baseInputStyle}
    ${({ after, before })=>getInputBorderRadius({
        after,
        before
    })}
    ${({ leftIcon })=>getInputPadding({
        combobox: true,
        leftIcon
    })}
    ${({ error, look })=>getInputTheme({
        error,
        look
    })}
    align-items: center;
    cursor: ${({ disabled, readOnly })=>!disabled && !readOnly ? 'pointer' : 'default'};
    display: inline-flex;
    overflow: hidden; // firefox
    resize: none;

    &.bf-combobox__combobox--hovered {
      ${({ disabled, error, labels, loading, look })=>getInputHoveredBorderColor({
        disabled,
        error,
        loading: loading === true && !!labels.loaderLabel,
        look
    })}
    }

    &.bf-combobox__combobox--focused {
      ${({ disabled, error, labels, loading, look })=>getInputFocusedBorderColor({
        disabled,
        error,
        loading: loading === true && !!labels.loaderLabel,
        look
    })}
    }

    &.bf-combobox__combobox--placeholder {
      ${({ disabled, error, labels, loading, look })=>getInputPlaceholderColor({
        disabled,
        error,
        loading: loading === true && !!labels.loaderLabel,
        look
    })}
    }
  }

  .bf-button__icon {
    border-radius: ${borderRadiusLarge}px;
    height: 40px;
    min-height: 0;
    padding: 5px;
    position: absolute;
    right: 5px;
    top: 5px;
    width: 40px;
    z-index: 1;

    [dir='rtl'] & {
      left: 5px;
      right: unset;
    }

    .bf-button__font-icon {
      font-size: 12px;
    }
  }

  .bf-listbox__standard {
    position: absolute;
    width: 100%;
    z-index: 1;
  }

  &.bf-combobox__standard--position-bottom {
    .bf-combobox__body {
      &:after {
        content: ' ';
        display: block;
        height: 5px;
        width: 100%;
        position: absolute;
        top: 100%;
      }
    }

    .bf-listbox__standard {
      top: calc(100% + 5px);
    }
  }

  &.bf-combobox__standard--position-top {
    .bf-combobox__body {
      &:before {
        content: ' ';
        display: block;
        height: 5px;
        width: 100%;
        position: absolute;
        bottom: 100%;
      }
    }

    .bf-listbox__standard {
      bottom: calc(100% + 5px);
    }
  }

  &.bf-combobox__standard--empty,
  &.bf-combobox__standard--loading {
    .bf-option__standard--disabled {
      color: ${BrandfolderHexColors.Text};
      text-align: center;
    }
  }
`;
/**
 * A `role="combobox"` with `role="listbox"` that contains `role="option"` elements.
 * `id` and `labels` props are required for accessibility. `onSelection` and `options` props are also required for functionality.
 *
 * When `editable` is `false`, this component re-creates the HTML `<select>` and `<option>` elements
 * with a `<div role="combobox">` that has more flexibility in styling and in rendering content.
 * It follows the W3C select-only combobox [example](https://w3c.github.io/aria-practices/examples/combobox/combobox-select-only.html).
 *
 * When `editable` is `true`, this component creates a searchable `role="listbox"` with `<input role="combobox">`.
 * It follows the W3C editable combobox with list autocomplete [example](https://w3c.github.io/aria-practices/examples/combobox/combobox-autocomplete-list.html).
 *
 * Add a placeholder by providing a first `option` with `option.placeholder = true`.
 * You may hide selecting this placeholder option with `showPlaceholderOption` set to `false`.
 *
 * When `InputLabelPositions.Left`is set, apply a `width` or `max-width` on `.bf-textfield__header`.
 * See Textfield [examples](https://brandfolder.com/designsystem/?path=/docs/components-molecules-textfields--examples)
 * for recommended CSS styling.
 *
 * https://www.w3.org/TR/wai-aria-1.2/#combobox
 *
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/combobox_role
 *
 * @param props StandardComboboxProps
 * @returns FunctionComponent<StandardComboboxProps>
 */ const StandardCombobox = (props)=>{
    const { allowCustomValue = false, allowOptionsWrapping = true, clearAfterCustomValue = false, closeOnClickAway = true, editable = false, labelPosition = InputLabelPositions.Top, look = InputLooks.Default, placement = ComboboxPlacements.BottomStart, search = true, searchDebounce = 200, searchMinLength = 0, showEmptyOption = true, showIconButton = true, showLabel = true, showLoadingOption = true, showPlaceholderOption = true, transition = Transitions.FadeInSlide, transitionDelay = 0, transitionDuration = 200, ...otherProps } = props;
    const themeContext = useContext(ThemeContext);
    return /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: themeContext || lightTheme
    }, /*#__PURE__*/ React.createElement(Combobox, {
        ...otherProps,
        allowCustomValue: allowCustomValue,
        allowOptionsWrapping: allowOptionsWrapping,
        clearAfterCustomValue: clearAfterCustomValue,
        closeOnClickAway: closeOnClickAway,
        editable: editable,
        labelPosition: labelPosition,
        look: look,
        placement: placement,
        search: search,
        searchDebounce: searchDebounce,
        searchMinLength: searchMinLength,
        showEmptyOption: showEmptyOption,
        showIconButton: showIconButton,
        showLabel: showLabel,
        showLoadingOption: showLoadingOption,
        showPlaceholderOption: showPlaceholderOption,
        transition: transition,
        transitionDelay: transitionDelay,
        transitionDuration: transitionDuration
    }));
};

export { BaseStandardCombobox, DEFAULT_EDITABLE_FILTER, DEFAULT_SELECT_FILTER, StandardCombobox };
//# sourceMappingURL=standard-combobox.js.map
